import React from "react";
import {
  FiPhoneCall,
  FiRefreshCcw,
  FiGlobe,
  FiCpu,
  FiMic,
  FiUsers,
  FiHeadphones,
  FiZap,
} from "react-icons/fi";
import { MdOutlineIntegrationInstructions } from "react-icons/md";
import AIAutomationTemplate from "./AIAutomationTemplate";

const AICallingAgent = () => {
  const features = [
    {
      title: "Outbound & Inbound Intelligence",
      description:
        "AI agents place high-volume outbound campaigns, answer inbound calls in real time, and hand off conversations to humans with contextual summaries.",
      icon: <FiPhoneCall />,
    },
    {
      title: "Voice Cloning Studio",
      description:
        "Train bespoke voice profiles that match your brand tone with controllable pitch, pacing, and emotion for every scripted or dynamic response.",
      icon: <FiMic />,
    },
    {
      title: "Automated Follow-Ups",
      description:
        "Trigger SMS, email, and callback sequences based on call outcomes to keep prospects engaged without manual intervention.",
      icon: <FiRefreshCcw />,
    },
    {
      title: "CRM & Dialer Integration",
      description:
        "Bi-directional connectors push transcripts, call scores, and next actions into Salesforce, HubSpot, Zoho, or custom CRMs instantly.",
      icon: <MdOutlineIntegrationInstructions />,
    },
    {
      title: "Multilingual Voice AI",
      description:
        "Serve customers in 30+ languages with locale-aware pronunciation, slang understanding, and dynamic script switching.",
      icon: <FiGlobe />,
    },
    {
      title: "Sentiment & Compliance Guardrails",
      description:
        "Real-time analytics detect sentiment shifts, script deviations, and compliance flags to keep every interaction on-brand and regulated.",
      icon: <FiCpu />,
    },
  ];

  const steps = [
    {
      title: "Conversation Blueprinting",
      description:
        "We analyze call scripts, objection handling, and KPIs to design AI-ready workflows mapped to your funnels.",
    },
    {
      title: "Voice & Persona Training",
      description:
        "Custom voices, intents, and guardrails are trained using historical recordings, FAQs, and compliance guides.",
    },
    {
      title: "System Integration",
      description:
        "Secure APIs connect dialers, CRMs, ticketing tools, and analytics so every call enriches your core systems.",
    },
    {
      title: "Pilot & Scale",
      description:
        "Launch controlled pilots, monitor live analytics, tune prompts, then scale across geographies and languages.",
    },
  ];

  const benefits = [
    "Handle 10x more calls without scaling headcount",
    "Reduce wait times with instant pick-up and smart routing",
    "Boost conversion through consistent follow-up cadence",
    "Gain full transcripts, QA scores, and insights per call",
    "Optimize agent training with AI-generated coaching tips",
    "Ensure compliance with script enforcement and auditing",
  ];

  const expertise = [

    "Deep telephony experience across Twilio, Plivo, Exotel, and in-house SIP",
    "Custom neural voice training pipelines with speaker diarization",
    "PCI, HIPAA, and GDPR-aware call data handling practices",
    "Contact center integrations with Five9, Genesys, and Freshdesk",
    "Prompt engineering for low-latency, context-rich responses",
    "24/7 monitoring and human-in-the-loop escalation frameworks",

  ];

  const image =[
{
  side_image:"../assets/images/ai-automation/1.png",
}

  ];

  const useCases = [
    {
      industry: "Fintech",
      title: "Loan Outreach Concierge",
      description:
        "Automated outbound campaigns qualify applicants, collect documents, and synchronize status updates back to LOS platforms.",
    },
    {
      industry: "Healthcare",
      title: "Appointment Coordination",
      description:
        "Inbound agent verifies insurance, reschedules appointments, and pushes updates to EHR systems within seconds.",
    },
    {
      industry: "E-commerce",
      title: "Post-Purchase Follow-Up",
      description:
        "Outbound post-delivery calls capture NPS, manage returns, and launch proactive win-back offers.",
    },
    {
      industry: "Real Estate",
      title: "Lead Nurturing Pod",
      description:
        "Round-the-clock calling agent pre-qualifies buyer inquiries, books site visits, and notifies brokers in CRM.",
    },
  ];

  const reasons = [
    "Telecom + AI engineering squad delivering carrier-grade uptime",
    "Library of pre-built call flows for sales, support, and collections",
    "Embedded analytics revealing conversion and sentiment trends",
    "Flexible deployment on cloud, on-prem, or hybrid data zones",
    "Human supervision portal for live monitoring and takeover",
    "Outcome-based engagement models aligned to SDR/Sales KPIs",
  ];

  return (
    <AIAutomationTemplate
      title="AI Calling Agent"
      heroDescription="Deploy autonomous voice agents that speak naturally, respect compliance, and keep your pipelines moving across outbound and inbound scenarios."
      about={{
        heading: "About Our AI Calling Agent Service",
        description:
          "We fuse conversational AI, telephony infrastructure, and CRM intelligence to create agents that call, converse, and convert like your best human reps—only faster and at scale.",
      }}
      features={features}
      steps={steps}
      benefits={benefits}
      expertise={expertise}
      image={image}
      useCases={useCases}
      reasons={reasons}
      ctaLabel="Book a Voice AI Demo"
      meta={{
        title: "AI Calling Agent | Voice Automation Services | Ayanshtech",
        description:
          "Outbound & inbound AI calling agents with voice cloning, multilingual support, CRM integration, and automated follow-ups.",
        ogTitle: "AI Calling Agent by Ayanshtech",
        ogDescription:
          "Scale voice operations with autonomous agents for outbound, inbound, and multilingual calling.",
        ogUrl: "http://ayanshtechnology.com/ai-calling-agent",
        canonical: "http://ayanshtechnology.com/ai-calling-agent",
      }}
    />
  );
};

export default AICallingAgent;


